/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Dmitriy Kvon
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Andrey Dubov
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.20.1
 */

import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { formQueryParamsString } from '@/main/sagas/utils/formQueryParamsString';

import { baseQuery } from '../../api/baseQuery';
import { FileUploadHeader } from '../../api/headers';
import { modalSetStatusAction } from '../modal/modalActions';
import { EModalStatus } from '../modal/types/modalTypes';
import { AttributeDictionaryPageSize, ECreateInquiryType } from './constants';
import {
    mapInquirySectionAndTopics,
    onChangeAttributeGetNewSlaveAttributes,
    onChangeAttributeMergeAttributesWithNew,
} from './mapping';
import type {
    FileId,
    ICreateInquiryAttributeDictionary,
    ICreateInquiryChangeAttribute,
    ICreateInquiryChangeAttributeQuery,
    ICreateInquiryGetAttributes,
    ICreateInquiryGetAttributesQueries,
    ICreateInquiryInitialise,
    ICreateInquirySave,
    IInquirySectionAndTopics,
    TCreateInquiryAttributeDictionaryQuery,
} from './types';

export const inquiryApi = createApi({
    reducerPath: 'inquiryApi',
    baseQuery,
    endpoints: (builder) => ({
        getSectionsAndTopics: builder.query<IInquirySectionAndTopics, ICreateInquiryInitialise>({
            query: ({ type, id }) => `inquiry/topic/${type}${id ? `?id=${id}` : ''}`,
            transformResponse: (response, _, { initialTopicCode }) =>
                mapInquirySectionAndTopics(response, initialTopicCode),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (e) {
                    dispatch(
                        modalSetStatusAction({
                            status: EModalStatus.failure,
                        }),
                    );
                }
            },
        }),
        getAttributes: builder.query<ICreateInquiryGetAttributes, ICreateInquiryGetAttributesQueries>({
            query: ({ topicCode, type, id }) => `inquiry/topic/${type}/${topicCode}${id ? `?id=${id}` : ''}`,
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (e) {
                    dispatch(
                        modalSetStatusAction({
                            status: EModalStatus.failure,
                        }),
                    );
                }
            },
        }),

        changeAttribute: builder.mutation<ICreateInquiryChangeAttribute, ICreateInquiryChangeAttributeQuery>({
            query: ({ attributeId, topicCode, type, id, masterAttributes }) => ({
                url: `inquiry/attribute/${type}/${topicCode}/${attributeId}${id ? `?id=${id}` : ''}`,
                method: 'POST',
                body: masterAttributes,
            }),
            async onQueryStarted({ topicCode, type, id }, { dispatch, queryFulfilled }) {
                try {
                    const data = await queryFulfilled;
                    if (data.data.customAttrs) {
                        const newSlaveAttributes = onChangeAttributeGetNewSlaveAttributes(topicCode, data.data);

                        dispatch(
                            inquiryApi.util.updateQueryData('getAttributes', { topicCode, type, id }, (draft) => {
                                draft.customAttrs = draft.customAttrs.map(
                                    onChangeAttributeMergeAttributesWithNew(newSlaveAttributes),
                                );
                                draft.serviceAttributes = {
                                    ...draft.serviceAttributes,
                                    ...(data.data.serviceAttributes || {}),
                                };
                            }),
                        );
                    }
                } catch (e) {
                    dispatch(
                        modalSetStatusAction({
                            status: EModalStatus.failure,
                        }),
                    );
                }
            },
        }),

        attachFile: builder.mutation<{ fileId: FileId }, { file: File; type: ECreateInquiryType }>({
            query: ({ file, type }) => {
                const data = new FormData();
                data.append('file', file);
                return {
                    url: `inquiry/attachment/${type}`,
                    method: 'POST',
                    headers: FileUploadHeader,
                    body: data,
                };
            },
        }),
        deleteFile: builder.mutation<void, { fileId: string }>({
            query: ({ fileId }) => ({
                url: `inquiry/attachment/${fileId}`,
                method: 'DELETE',
            }),
        }),

        save: builder.mutation<{ id?: string; fileAttachedWithErrors?: boolean }, ICreateInquirySave>({
            query: (data) => ({
                url: 'inquiry/create',
                method: 'POST',
                body: data,
            }),
        }),
        postAttributeDictionary: builder.mutation<
            ICreateInquiryAttributeDictionary,
            TCreateInquiryAttributeDictionaryQuery
        >({
            // TODO перенести квери параметры в body
            query: ({ searchValue, from, topicCode, attributeId, type, id, masterAttributes }) => {
                const queryString = formQueryParamsString({
                    id: id || '',
                    from: from || 0,
                    searchString: searchValue ? encodeURIComponent(`%${searchValue}%`) : '',
                    size: AttributeDictionaryPageSize,
                });
                return {
                    url: `inquiry/dictionary/${type}/${topicCode}/${attributeId}${queryString}`,
                    method: 'POST',
                    body: masterAttributes,
                };
            },
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (e) {
                    dispatch(
                        modalSetStatusAction({
                            status: EModalStatus.failure,
                        }),
                    );
                }
            },
        }),
    }),
});
